import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import type { ForgeQuery, FieldConfig } from '../../../components/ForgeQueryBuilder';
import { mergeDefaultQueryWithRemembered } from '../../../components/ForgeQueryBuilder/query';
import type { TabListProps, TabListInitialState, TabListStatItem } from './types';
import type { PageListRef } from '../../../components/Templates/PageList';

/** 解析 URL 中的 q 参数为查询对象 */
export const parseTabListQueryFromSearch = (search: string): ForgeQuery | undefined => {
  const searchParams = new URLSearchParams(search);
  const value = searchParams.get('q');
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (e) {
    try {
      const parsed = JSON.parse(decodeURIComponent(value));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (err) {
      return undefined;
    }
  }
  return undefined;
};

/** 解析路由与入参，生成列表的初始状态 */
export const buildTabListInitialState = (params: {
  search: string;
  defaultTabKey?: string;
  queryFields: FieldConfig[];
  getDefaultQuery?: (tabKey?: string) => ForgeQuery;
}): TabListInitialState => {
  const searchParams = new URLSearchParams(params.search);
  const forcedQuery = parseTabListQueryFromSearch(params.search);
  const forcedViewMode = searchParams.get('v') || undefined;
  const forcedTabKey = searchParams.get('t') || undefined;
  const initialTabKey = forcedTabKey || params.defaultTabKey;
  const defaultQuery = params.getDefaultQuery ? params.getDefaultQuery(initialTabKey) : {};

  const { query } = mergeDefaultQueryWithRemembered(params.queryFields, forcedQuery ? undefined : defaultQuery);
  return {
    forcedQuery,
    forcedViewMode,
    forcedTabKey,
    initialTabKey,
    initialQuery: forcedQuery || query,
  };
};

export function useTabList<T extends object>(props: TabListProps<T>) {
  const {
    queryFields,
    defaultTabKey,
    viewModes,
    fetchKpiStats,
    fetchTabStats,
    getDefaultQuery,
    refreshKey,
  } = props;

  const location = useLocation();
  const [initialSearch] = useState(location.search);

  // 初始化状态
  const initialState = useMemo(() => buildTabListInitialState({
    search: initialSearch,
    defaultTabKey,
    queryFields,
    getDefaultQuery,
  }), [initialSearch, defaultTabKey, queryFields, getDefaultQuery]);

  const listRef = useRef<PageListRef>(null);

  // Refs
  const currentTabKeyRef = useRef<string | undefined>(initialState.initialTabKey);
  const lastRefreshKeyRef = useRef<number | undefined>(refreshKey);
  const hasAppliedForcedTabRef = useRef(false);
  /** 防重入守卫：阻止 refreshStats → search → fetcher → refreshStats 级联循环 */
  const isRefreshingRef = useRef(false);

  // States
  const [initFinished, setInitFinished] = useState(!fetchTabStats);
  const [kpiStats, setKpiStats] = useState<TabListStatItem[]>([]);
  const [tabStats, setTabStats] = useState<TabListStatItem[]>([]);
  const [activeIndex, setActiveIndex] = useState<string>('0'); // index in tabStats

  // View Mode
  const initialViewMode = useMemo<string>(() => {
    const matched = viewModes.find(v => v.mode === initialState.forcedViewMode);
    return matched?.mode || (viewModes.length > 0 ? viewModes[0].mode : 'table');
  }, [initialState.forcedViewMode, viewModes]);
  const [currentViewMode, setCurrentViewMode] = useState<string>(initialViewMode);

  /** 刷新统计并同步场景，必要时触发列表重拉。内含防重入守卫。 */
  const refreshStats = useCallback(async (query: ForgeQuery = {}) => {
    // 防重入：如果正在刷新中，跳过本次调用，避免级联循环
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      if (!fetchKpiStats && !fetchTabStats) {
        setKpiStats([]);
        setTabStats([]);
        return;
      }

      const [kpis, tabs] = await Promise.all([
        fetchKpiStats ? fetchKpiStats(query) : Promise.resolve([]),
        fetchTabStats ? fetchTabStats(query) : Promise.resolve([]),
      ]);

      setKpiStats(kpis || []);
      setTabStats(tabs || []);

      if (!tabs || tabs.length === 0) {
        if (!currentTabKeyRef.current) currentTabKeyRef.current = defaultTabKey;
        setActiveIndex('0');
        return;
      }

      const prevTabKey = currentTabKeyRef.current;
      const desiredTabKey = prevTabKey || defaultTabKey;
      let nextIndex = 0;

      // 如果初始化有指定的 tab，并且还没应用过
      if (initialState.forcedTabKey && !hasAppliedForcedTabRef.current) {
        const forcedIndex = tabs.findIndex(t => t.key === initialState.forcedTabKey);
        hasAppliedForcedTabRef.current = true;
        if (forcedIndex >= 0) {
          nextIndex = forcedIndex;
        }
      } else if (desiredTabKey) {
        const found = tabs.findIndex(t => t.key === desiredTabKey);
        if (found >= 0) nextIndex = found;
      }

      const nextTabKey = tabs[nextIndex]?.key || desiredTabKey;
      currentTabKeyRef.current = nextTabKey || prevTabKey || defaultTabKey;
      setActiveIndex(String(nextIndex));

      // 若 Tab 发生非预期变更（比如原先的 Tab 已不存在并回滚到了另一个 Tab），则重启查询
      if (nextTabKey && nextTabKey !== prevTabKey && listRef.current) {
        listRef.current.search(listRef.current.getQuery() || {});
      }
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [fetchKpiStats, fetchTabStats, defaultTabKey, initialState.forcedTabKey]);

  // 初始加载
  const initialQueryStr = useMemo(() => JSON.stringify(initialState.initialQuery), [initialState.initialQuery]);

  useEffect(() => {
    let cancelled = false;
    if (!fetchTabStats) {
      setInitFinished(true);
      return;
    }
    setInitFinished((prev) => prev ? true : false);
    refreshStats({ filter: initialState.initialQuery.filter, sort: initialState.initialQuery.sort }).finally(() => {
      if (!cancelled) setInitFinished(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchTabStats, initialQueryStr, refreshStats]);

  // 外部刷新
  useEffect(() => {
    if (!initFinished) return;
    if (refreshKey === undefined) return;
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;

    if (listRef.current) {
      listRef.current.reload();
    } else {
      refreshStats(initialState.initialQuery);
    }
  }, [refreshKey, initFinished, initialState.initialQuery, refreshStats]);

  // View Mode Sync
  useEffect(() => {
    if (viewModes.some(v => v.mode === currentViewMode)) return;
    setCurrentViewMode(initialViewMode);
  }, [currentViewMode, initialViewMode, viewModes]);

  // 内部切换 Tab
  const handleTabChange = useCallback((indexStr: string) => {
    setActiveIndex(indexStr);
    const index = Number(indexStr);
    const stat = tabStats[index];

    if (stat) {
      currentTabKeyRef.current = stat.key;
      if (listRef.current) {
        listRef.current.search(listRef.current.getQuery() || {});
      }
    }
  }, [tabStats]);

  return {
    listRef,
    initialState,
    initFinished,
    kpiStats,
    tabStats,
    activeIndex,
    currentTabKey: currentTabKeyRef.current,
    currentTabKeyRef,
    currentViewMode,
    setCurrentViewMode,
    handleTabChange,
    refreshStats,
  };
}
